import { AddressGenerator } from '@/components/helpers/formatters/AddressBuilder';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { MlCopyBlock } from '@/components/molecules/ml-copy-block/ml-copy-block';
import { MlCard } from '@/components/molecules/ml-card/ml-card';
import { MlHeader } from '@/components/molecules/ml-header/ml-header';
import { MlAccordionItem } from '@/components/molecules/ml-accordion/ml-accordion';
import { OrCarouselHero } from '@/components/organisms/or-carousel-hero/or-carousel-hero';
import { OrContentBlock } from '@/components/organisms/or-content-block/or-content-block';
import { OrServiceAreas } from '@/components/organisms/or-service-areas/or-service-areas';
import { OrPromotionalBlock } from '@/components/organisms/or-promotional-block/or-promotional-block';
import { OrFeaturedVideo } from '@/components/organisms/or-featured-video/or-featured-video';
import { OrContentMediaBlock } from '@/components/organisms/or-content-media-block/or-content-media-block';
import { OrFaqList } from '@/components/organisms/or-faq-list/or-faq-list';
import { OrFaqListCS } from '@/components/organisms/or-faq-list/or-faq-list.cms';
import { TmDesignCenterDetailsClient } from './tm-design-center-detail.client';
import RenderComponents from '@/components/helpers/cms/RenderComponents';
import { RenderRichText } from '@/components/helpers/cms/RenderRichText';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { ISlide } from '@/components/organisms/or-carousel-hero/or-carousel-hero.types';

import {
  parseImageFromCMS,
  parseIconFromCMS,
  parsePromos,
} from '@/lib/contentstack-sdk/utils/parseHelpers';
import { formatModelHomeUrl } from '@/components/helpers/formatters/model-homes';

import { ITmDesignCenterDetailsProps } from './tm-design-center-details.types';
import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';
import { EOrContentMediaBlockSize } from '@/components/organisms/or-content-media-block/or-content-media-block.types';
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import {
  EClickableTypes,
  EClickableVariants,
  EClickableSize,
  EClickableTarget,
} from '@/components/atoms/at-clickable/at-clickable.types';
import {
  EDividerColors,
  EDividerVariants,
  EDividerDirectionalVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { EThemes, EBlockAlignment } from '@/lib/contentstack-sdk/types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { EMlHeaderSize } from '@/components/molecules/ml-header/ml-header.types';

// Assume you implement these on server:
import { AtMedia } from '@/components/atoms/at-media/at-media';
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';

type ITempSlide = {
  id: string;
  image: IAtMediaProps | undefined;
  // Include any other properties ISlide has that are being mapped
};

export const TmDesignCenterDetails = ({
  block,
  modelHomeData,
  calendlyLink,
  promotions,
  pageStudio,
}: ITmDesignCenterDetailsProps) => {
  const modelHomeGridProps = (() => {
    const isSingleModel = modelHomeData?.length === 1;
    const defaultTitle = isSingleModel
      ? `Model Home at ${pageStudio?.Name}`
      : `Model Homes at ${pageStudio?.Name}`;
    const defaultDescription = isSingleModel
      ? 'Tour the model home, conveniently located next to our Design Studio'
      : 'Tour these model homes, conveniently located next to our Design Studio';

    return {
      ...block.model_home_grid,
      title: block.model_home_grid?.title || defaultTitle,
      description: block.model_home_grid?.description || defaultDescription,
    };
  })();

  const leadGenForm = block.lead_gen_form_design_studio?.[0];

  return (
    <div data-theme="light">
      <OrCarouselHero
        textContent={
          <>
            <AtHeader element="h1" alt className="pb-10">
              {block.template_heading || block.design_studio_name}
            </AtHeader>
            {!block.is_virtual && (
              <>
                <AtText
                  type={ETextVariants.LABEL}
                  className="flex items-center gap-x-1 pb-3.25 text-18 font-semibold uppercase tracking-[4%] text-skin-tertiary"
                >
                  <AtIcon
                    icon={EIcons.LOCATION}
                    color="var(--color-text-tertiary)"
                    className="text-24"
                  />
                  Location
                </AtText>
                <div className="pb-6">
                  <AddressGenerator studioData={pageStudio || null} />
                </div>
              </>
            )}

            <div className="flex gap-6">
              <AtClickable
                type={EClickableTypes.LINK}
                href={`tel:${block.phone_number}`}
                size={EClickableSize.LG}
                interactLocation="tm-design-center-detail"
              >
                {block.phone_number}
              </AtClickable>

              {!block.is_virtual && block.directions_link && (
                <>
                  <AtDivider
                    customClasses="h-35"
                    direction={EDividerDirectionalVariants.VERTICAL}
                    color={EDividerColors.BLACK}
                    intent={EDividerVariants.THICK}
                  />
                  <AtClickable
                    type={EClickableTypes.LINK}
                    href={block.directions_link}
                    target={EClickableTarget.BLANK}
                    size={EClickableSize.LG}
                    interactLocation="tm-design-center-detail"
                    label="Get Directions"
                  />
                </>
              )}
            </div>

            <div className="pt-7">
              <AtText
                type={ETextVariants.LABEL}
                className="pb-3.5 text-18 font-semibold uppercase text-skin-tertiary"
              >
                Hours
              </AtText>
              <ul className="pb-10">
                {block.hours.map(({ dates }) => (
                  <li key={dates._metadata.uid}>
                    <AtText>
                      <span className="font-semibold">{dates.days}:</span>{' '}
                      {dates.hours}
                    </AtText>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-4.5 md:w-[80%] xl:w-[90%]">
                <TmDesignCenterDetailsClient
                  formId={leadGenForm.form_id}
                  formName={leadGenForm.title}
                  heading={leadGenForm.heading}
                  headingElement={
                    leadGenForm.heading_element
                      ?.select as THeaderSupportedElements
                  }
                  headingVariant={
                    leadGenForm.heading_variant
                      ?.select as THeaderSupportedElements
                  }
                  description={leadGenForm.description}
                  formType={leadGenForm.form_type}
                  isInModal={leadGenForm.is_in_modal}
                  topSpacing={leadGenForm.top_spacing?.spacing_value}
                  bottomSpacing={leadGenForm.bottom_spacing?.spacing_value}
                />
                {calendlyLink && (
                  <AtClickable
                    variant={EClickableVariants.PRIMARY}
                    iconPosition="right"
                    href={calendlyLink || block?.schedule_link}
                    target={EClickableTarget.BLANK}
                    className="flex w-full items-center justify-center gap-1"
                    interactLocation="tm-design-center-detail"
                    label="Schedule a Meeting"
                  />
                )}
              </div>
            </div>
          </>
        }
        className="mb-120"
        slideData={
          block.hero_gallery
            .map(({ global_field, _metadata }) => ({
              id: _metadata.uid,
              image: parseImageFromCMS(global_field) as
                | IAtMediaProps
                | undefined,
            }))
            .filter(
              (slide): slide is ITempSlide & { image: IAtMediaProps } =>
                slide.image !== undefined,
            ) as ISlide[]
        }
      />

      <OrContentBlock
        data-testid="or-content-block"
        RenderLeftContent={() => (
          <MlCopyBlock title={block.design_center_info?.left_content?.title}>
            <RenderRichText
              body={block.design_center_info?.left_content?.description}
            />
          </MlCopyBlock>
        )}
        theme={EThemes.DARK}
        topSpacing="xl"
        bottomSpacing="xl"
      >
        {block.design_center_info?.card?.map(
          ({ _metadata, title, description, icon }) => (
            <div key={_metadata.uid} className="flex self-start">
              <MlCard
                iconName={parseIconFromCMS(icon)}
                theme={EThemes.DARK}
                title={title}
                body={description}
                headerElement="h3"
                className="[&_p]:paragraph-base rounded-none !px-0 [&_h3]:font-Figtree [&_p]:text-22 [&_p]:lg:text-2xl"
              />
            </div>
          ),
        )}
      </OrContentBlock>

      {modelHomeData.length > 0 && (
        <section className="container py-120">
          <MlHeader
            head={modelHomeGridProps.title}
            body={modelHomeGridProps.description}
            headElement={modelHomeGridProps.heading_element?.select || 'h2'}
            size={EMlHeaderSize.SMALL}
            className="pb-14.5"
          />
          {pageStudio && (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
              {modelHomeData.map((home) => {
                const image =
                  home.cms?.card_image?.mobile_image_url?.url || home.Image;
                return (
                  <MlCard
                    key={home.objectID}
                    imageProps={{
                      src: { mobile: image, tablet: image, desktop: image },
                      alt: '',
                      quality: 65,
                    }}
                    variant={EMlCardsTypes.HOME_PLAN}
                    showFavouriteButton={false}
                    href={formatModelHomeUrl(
                      pageStudio?.CsName || pageStudio?.Name,
                      home?.Plan?.Name,
                      home?.ExteriorStyle?.Name,
                    )}
                    homePlanProps={{
                      id: home.Id,
                      name: home?.Plan?.Name,
                      description: home?.ExteriorStyle?.Name,
                      features: [
                        'Model Home',
                        `${home?.Plan?.Bedrooms} Bed`,
                        `${home?.Plan?.Bathrooms} Bath`,
                        `${home?.SquareFeet} SQ.Ft`,
                      ],
                      tags: [],
                    }}
                  />
                );
              })}
            </div>
          )}
        </section>
      )}

      {pageStudio && <OrServiceAreas designStudio={[pageStudio]} />}

      {promotions.length > 0 && (
        <OrPromotionalBlock
          topSpacing="xl"
          bottomSpacing="lg"
          cta={{
            label: block.promo_block?.link?.label || 'Explore More Offers',
            variant: block.promo_block?.link?.variant || 'PRIMARY',
            outline: block.promo_block?.link?.outline ?? true,
            size: block.promo_block?.link?.size || 'LG',
            target: block.promo_block?.link?.target || 'SELF',
            type: block.promo_block?.link?.type || EClickableTypes.CTA,
            url: block.promo_block?.link?.url || {
              href: '/promo',
              title: 'Explore More Offers',
            },
          }}
          autoplay={false}
          blockAlignment={EBlockAlignment.CENTER}
          title={block.promo_block?.title || 'Build Your Dream Home For Less'}
          theme={EThemes.LIGHT}
          promos={parsePromos({ promos: promotions, isPromoBlock: true })}
        />
      )}

      {block.featured_media?.video_url && (
        <OrFeaturedVideo
          url={block.featured_media.video_url}
          playButtonAriaLabel={
            block.featured_media.video_button_aria_label || 'Play Video'
          }
          thumbnail={parseImageFromCMS(block.featured_media.video_thumbnail)}
          title={block.featured_media.video_title}
          description={
            <RenderRichText body={block.featured_media.description} />
          }
        />
      )}

      {!block.is_virtual && (
        <OrContentMediaBlock
          theme={EThemes.LIGHT}
          size={EOrContentMediaBlockSize.SM}
          RenderLeftContent={() => (
            <span className="block aspect-square h-full">
              <AtMedia
                sizes="(max-width: 1023px) 100vw, 50vw"
                {...parseImageFromCMS(block.directions_block?.image)!}
                className="h-full"
              />
            </span>
          )}
          topSpacing="lg"
          bottomSpacing="xl"
        >
          <MlCopyBlock title={block.directions_block?.title}>
            <AtText>
              {block.directions_block?.description ||
                `Tour our Model Homes, conveniently located next to the ${block.design_studio_name} Design Center.`}
            </AtText>
            {pageStudio && <AddressGenerator studioData={pageStudio} />}

            {block.directions_link && (
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                type={EClickableTypes.LINK}
                href={block.directions_link}
                target={EClickableTarget.BLANK}
                interactLocation="tm-design-center-detail"
                label="See Directions"
              />
            )}
          </MlCopyBlock>
        </OrContentMediaBlock>
      )}

      {block.body?.length > 0 && <RenderComponents body={block.body} />}

      {block.faq?.[0]?._content_type_uid === 'accordion' && (
        <OrFaqList
          RenderLeftContent={() => (
            <MlCopyBlock title="Design Studio FAQs">
              <AtText>
                We&apos;re here to help you figure out everything you need to
                know to have a successful and stress-free homebuilding process.
              </AtText>
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                href="/faq"
                interactLocation="tm-design-center-detail"
              >
                See All FAQs
              </AtClickable>
            </MlCopyBlock>
          )}
          theme={EThemes.LIGHT}
          topSpacing="lg"
          bottomSpacing="lg"
          topDivider
        >
          {block.faq[0].items.map((item, index) => (
            <MlAccordionItem
              key={index}
              index={index}
              title={item.accordion_item.title}
            >
              <RenderRichText body={item.accordion_item.body} />
            </MlAccordionItem>
          ))}
        </OrFaqList>
      )}

      {block.faq?.[0]?._content_type_uid === 'faq_list' && (
        <OrFaqListCS block={block.faq[0]} />
      )}
    </div>
  );
};
